import { defer } from 'react-router-dom'

import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'

import type { LoaderFunction } from 'react-router-dom'

const MAX_SCORES = 4

export interface ByTimeframeResponse {
  data_points: {
    offset: number
    values: number[]
  }
}

export interface ByScoreResponse {
  data_points: {
    remainder: number
    top: { count: number; title: string }[]
  }
}

export type DashboardLoaderResponse = {
  period: string
  timeIncrement: number
  viewsByScore: Promise<ByScoreResponse>
  viewsByTimeframe: Promise<ByTimeframeResponse>
  commentsByScore: Promise<ByScoreResponse>
  commentsByTimeframe: Promise<ByTimeframeResponse>
  favoritesByScore: Promise<ByScoreResponse>
  favoritesByTimeframe: Promise<ByTimeframeResponse>
}

const dashboardLoader: LoaderFunction = async ({ request }) => {
  const session = useGlobalDataStore.getState().globalData.session

  if (!session.hasPremiumFeatures()) {
    throw new Response('Premium Required', { status: 403, statusText: 'premium_required' })
  }

  const url = new URL(request.url)

  const period = url.searchParams.get('period') ?? '12m'

  const today = new Date()
  const startDate = new Date(today)

  const splitIdx = period.search(/[a-z]/)
  const periodNum = parseInt(period.substring(0, splitIdx), 10)
  const periodUnit = period.substring(splitIdx).toLowerCase()
  if (periodUnit === 'm') {
    startDate.setMonth(startDate.getMonth() - periodNum)
  } else if (periodUnit === 'd') {
    startDate.setDate(startDate.getDate() - periodNum)
  }

  const byScorePayload = {
    start_date: startDate,
    end_date: today,
    max_identified: MAX_SCORES,
  }

  const timeIncrement = (today.getTime() - startDate.getTime()) / 1000 / periodNum
  const byTimeframePayload = {
    start_date: startDate,
    end_date: today,
    time_increment: timeIncrement,
  }

  const viewsByScore = http.loader.post(ApiRoutes.social.viewsByScore(), byScorePayload)
  const viewsByTimeframe = http.loader.post(ApiRoutes.social.viewsByTimeframe(), byTimeframePayload)
  const commentsByScore = http.loader.post(ApiRoutes.social.commentsByScore(), byScorePayload)
  const commentsByTimeframe = http.loader.post(
    ApiRoutes.social.commentsByTimeframe(),
    byTimeframePayload
  )
  const favoritesByScore = http.loader.post(ApiRoutes.social.favoritesByScore(), byScorePayload)
  const favoritesByTimeframe = http.loader.post(
    ApiRoutes.social.favoritesByTimeframe(),
    byTimeframePayload
  )

  return defer({
    period,
    timeIncrement,
    viewsByScore,
    viewsByTimeframe,
    commentsByScore,
    commentsByTimeframe,
    favoritesByScore,
    favoritesByTimeframe,
  })
}

export default dashboardLoader
